import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BFXAppNotificationType, INotificationEvent } from "shared/cubiq-services";

import { BfxPopupNotificatonSettingsService } from "../../bfx-common/services/bfx-popup-notifications-settings.service";
import { BfxPopupEventSetting } from "../../bfx-user-settings/model/bfx-popup-event-setting";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NotificationService } from "shared/cubiq-services/lookup/notification/impl/NotificationService";
import { BFXNotificationType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { BfxAppEventSetting } from "app/bfx-user-settings/model/bfx-app-event-setting";
import { BfxAppNotificatonSettingsService } from "app/bfx-common/services/bfx-app-notifications-settings.service";

/**
 * Service that manages the mapping between an BFXAppNotificationType and the user setting that governs the
 * application notification for that event type. It will return the correct setting based on any order type that
 * is available in the payoad of the event and supports mapping of Limit types to algo types
 */
@Injectable()
export class BfxApplicationSettingProviderService {
  private notifMap: Map<BFXAppNotificationType, BfxAppEventSetting> = new Map();
  private loginNotifMap: Map<BFXAppNotificationType, BfxAppEventSetting> = new Map();
  public ready: Promise<void>;
  constructor(
    private appNotificationSettingsService: BfxAppNotificatonSettingsService,
    private notifService: NotificationService,
  ) {
    this.initializeSettingsAndSubscriptions();
  }

  // load the app notification settings, if they are not saved to the profile,
  // we need to load a default per user, that last part still needs to be completed
  private initializeSettingsAndSubscriptions(): void {
    this.notifMap.clear();
    this.loginNotifMap.clear();

    // Ready when we've ready in both groups of settings
    this.ready = Promise.all([
      this.appNotificationSettingsService.getAppNotificationsSettings(),
      this.appNotificationSettingsService.getBmarkAppNotificationsSettings(),
    ]).then(([defaultNotificationSettings, bmarkAppNotificationSettings]) => {
      this.updateSettings(defaultNotificationSettings);
      this.updateSettings(bmarkAppNotificationSettings);
      this.notifService.setAppNotificationTypes(this.notifMap.keys());

      //once both are got we subscribe for updates
      this.appNotificationSettingsService
        .getAppSettingsUpdateStream()
        .subscribe((settingsUpdate) => {
          this.updateSettings(settingsUpdate);
          this.notifService.setAppNotificationTypes(this.notifMap.keys());
        });
      this.appNotificationSettingsService
        .getAppBmarkSettingsUpdateStream()
        .subscribe((settingsUpdate) => {
          this.updateSettings(settingsUpdate);
          this.notifService.setAppNotificationTypes(this.notifMap.keys());
        });
    });
  }

  private updateSettings(settings: Array<BfxAppEventSetting>) {
    settings.forEach((event) => {
      if (event.enabled) {
        this.notifMap.set(event.type, event);
      } else {
        this.notifMap.delete(event.type);
      }
      const isLoginAppType =
        event.returnOnLogin &&
        (event.type === BFXAppNotificationType.PART_FILLED ||
          event.type === BFXAppNotificationType.FILLED ||
          event.type === BFXAppNotificationType.CANCELLED ||
          event.type === BFXAppNotificationType.EXPIRED ||
          event.type === BFXAppNotificationType.ENTERED ||
          event.type === BFXAppNotificationType.ALGO_PART_FILLED ||
          event.type === BFXAppNotificationType.ALGO_FILLED ||
          event.type === BFXAppNotificationType.ALGO_CANCELLED ||
          event.type === BFXAppNotificationType.ALGO_EXPIRED ||
          event.type === BFXAppNotificationType.ALGO_ENTERED ||
          event.type === BFXAppNotificationType.APP_BMARK_FILLED ||
          event.type === BFXAppNotificationType.APP_BMARK_CANCELLED ||
          event.type === BFXAppNotificationType.APP_BMARK_ENTERED);
      if (isLoginAppType) {
        this.loginNotifMap.set(event.type, event);
      } else {
        this.loginNotifMap.delete(event.type);
      }
    });
  }

  public getLoginTypes(): Iterable<BFXAppNotificationType> {
    return this.loginNotifMap.keys();
  }

  public getNotificationSetting(event: INotificationEvent): BfxAppEventSetting {
    let requestType;
    if (event.orderNotifData) {
      // Order events
      requestType = event.orderNotifData.requestedOrderType;
    } else if (event.tradeNotifData) {
      // For trade events
      requestType = event.tradeNotifData.requestedOrderType;
    }
    const isAlgo: Boolean =
      requestType == CubiqTS.RequestOrderType.ALGO ||
      requestType == CubiqTS.RequestOrderType.BMALGO;

    let retSetting: BfxAppEventSetting = null;
    // We need to use the Algo-specific setting rather than the general setting
    let settingType: BFXAppNotificationType;
    switch (event.notifType) {
      case BFXNotificationType.CANCELLED:
        settingType = isAlgo
          ? BFXAppNotificationType.ALGO_CANCELLED
          : BFXAppNotificationType.CANCELLED;
        break;
      case BFXNotificationType.EXPIRED:
        settingType = isAlgo ? BFXAppNotificationType.ALGO_EXPIRED : BFXAppNotificationType.EXPIRED;
        break;
      case BFXNotificationType.SUSPENDED:
        settingType = BFXAppNotificationType.SUSPENDED;
        break;
      case BFXNotificationType.PART_FILLED:
        settingType = isAlgo
          ? BFXAppNotificationType.ALGO_PART_FILLED
          : BFXAppNotificationType.PART_FILLED;
        break;
      case BFXNotificationType.FILLED:
        settingType = isAlgo ? BFXAppNotificationType.ALGO_FILLED : BFXAppNotificationType.FILLED;
        break;
      case BFXNotificationType.MODIFIED:
        settingType = isAlgo
          ? BFXAppNotificationType.ALGO_MODIFIED
          : BFXAppNotificationType.MODIFIED;
        break;
      case BFXNotificationType.ACTIVATED:
        settingType = isAlgo
          ? BFXAppNotificationType.ALGO_ACTIVATED
          : BFXAppNotificationType.ACTIVATED;
        break;
      case BFXNotificationType.BMARK_NEW_ORDERS:
        settingType = BFXAppNotificationType.APP_BMARK_NEW_ORDERS;
        break;
      case BFXNotificationType.BMARK_CANCELLED:
        settingType = BFXAppNotificationType.APP_BMARK_CANCELLED;
        break;
      case BFXNotificationType.BMARK_MODIFIED:
        settingType = BFXAppNotificationType.APP_BMARK_MODIFIED;
        break;
      case BFXNotificationType.BMARK_FILLED:
        settingType = BFXAppNotificationType.APP_BMARK_FILLED;
        break;
      case BFXNotificationType.LIMITALGO_ORDER_ENTERED:
        settingType = BFXAppNotificationType.ENTERED;
        break;
      case BFXNotificationType.BMARK_ORDER_ENTERED:
        settingType = BFXAppNotificationType.APP_BMARK_ENTERED;
        break;
    }

    if (settingType) {
      retSetting = this.notifMap.get(settingType);
    }

    if (!retSetting) {
      // If we can't find Algo setting (either it hasn't been migrated yet or doesn't exist)
      // fall back to the standard limit setting
      retSetting = this.notifMap.get(BFXAppNotificationType.ENTERED);
    }
    return retSetting;
  }
}
